import React from 'react'
import * as ROUTES from 'constants/routes'
import { Box, Button, Flex, Link, Text, Table } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { formatDate } from 'date-fns'
import { TableEmptyState } from 'components/Table/TableEmptyState'
import { useUserPrescreenedPositionsContextValue } from '../context/UserPrescreenedPositionsProvider'

type PrescreenedPositionItem = ReturnType<
  typeof useUserPrescreenedPositionsContextValue
>['state']['data']['items'][number]

const columns: ColumnDef<PrescreenedPositionItem>[] = [
  {
    header: 'Company',
    accessorKey: 'companyName',
    cell: ({ row }) => (
      <Text>
        {row.original.companyName} (
        <Link
          to={ROUTES.COMPANY_DETAIL.replace(
            ':companyId',
            row.original.companyId.toString()
          )}
          target="_blank"
        >
          {row.original.companyId}
        </Link>
        )
      </Text>
    ),
  },
  {
    header: 'Position',
    accessorKey: 'positionName',
    cell: ({ row }) => (
      <Text>
        {row.original.positionName} (
        <Link
          to={ROUTES.POSITION_DETAIL.replace(
            ':positionId',
            row.original.positionId.toString()
          )}
          target="_blank"
        >
          {row.original.positionId}
        </Link>
        )
      </Text>
    ),
  },
  {
    header: 'Added',
    accessorKey: 'createdAt',
    cell: ({ row }) => {
      const value = row.original.createdAt
      if (!value) return '—'
      return (
        <Text title={formatDate(value, 'MMM dd, yyyy, hh:mm a')}>
          {formatDate(value, 'MMM dd, yyyy')}
        </Text>
      )
    },
  },
]

export const UserPrescreenedPositionsTable = () => {
  const {
    state: { data, isFetching, canGoPrevious, canGoNext },
    actions: { goToPreviousPage, goToNextPage },
  } = useUserPrescreenedPositionsContextValue()

  if (data.items.length === 0) {
    return (
      <TableEmptyState
        isFetching={isFetching}
        emptyMessage="User is not prescreened for any company positions."
        emptyStateHeight={50}
      />
    )
  }

  return (
    <Box>
      <Table columns={columns} data={data.items} textVerticalAlign="top" />
      {canGoPrevious || canGoNext ? (
        <Flex mt={3} justifyContent="flex-end" style={{ gap: 8 }}>
          <Button
            variant="secondary"
            disabled={!canGoPrevious || isFetching}
            onClick={goToPreviousPage}
          >
            Previous
          </Button>
          <Button
            variant="secondary"
            disabled={!canGoNext || isFetching}
            onClick={goToNextPage}
          >
            Next
          </Button>
        </Flex>
      ) : null}
    </Box>
  )
}
